import { useCallback, useRef } from "react";
import { FiInfo, FiX } from "react-icons/fi";
import { RichTextEditor } from "newui/components/ui/RichTextEditor";
import InfoTooltip from "newui/components/InfoTooltip";
import type { CriterionScore } from "../../../types/scoring.type";

interface CriterionScoringRowProps {
	criterion: CriterionScore;
	maxScore: number;
	readOnly?: boolean;
	showGuideHint?: boolean;
	onCommentChange: (rfxCriteriaId: number, comment: string | null) => void;
	onScoreChange: (rfxCriteriaId: number, score: number | null) => void;
	onFocus?: (rfxCriteriaId: number, element: HTMLDivElement) => void;
	onShowGuide?: () => void;
}

export const CriterionScoringRow: React.FC<CriterionScoringRowProps> = ({
	criterion,
	maxScore,
	readOnly = false,
	onScoreChange,
	onCommentChange,
	onFocus,
	showGuideHint,
	onShowGuide,
}) => {
	const rowRef = useRef<HTMLDivElement>(null);
	const handleScoreChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const raw = e.target.value.trim();
			if (raw === "") {
				onScoreChange(criterion.rfxCriteriaId, null);
				return;
			}
			const parsed = parseInt(raw, 10);
			if (isNaN(parsed)) {
				return;
			}
			onScoreChange(criterion.rfxCriteriaId, parsed);
		},
		[criterion.rfxCriteriaId, onScoreChange]
	);

	const handleCommentChange = useCallback(
		(html: string) => {
			const isEmpty = !html || html === "<p><br></p>" || html.trim() === "";
			onCommentChange(criterion.rfxCriteriaId, isEmpty ? null : html);
		},
		[criterion.rfxCriteriaId, onCommentChange]
	);

	const handleRowFocus = useCallback(() => {
		if (onFocus && rowRef.current) {
			onFocus(criterion.rfxCriteriaId, rowRef.current);
		}
	}, [criterion.rfxCriteriaId, onFocus]);

	return (
		<div
			ref={rowRef}
			className="rounded-lg border border-gray-200 p-4 space-y-3"
			data-testid={`criterion-row-${criterion.rfxCriteriaId}`}
			onFocusCapture={handleRowFocus}
		>
			<div className="flex items-center gap-3">
				<span className="inline-flex items-center text-sm font-semibold text-gray-900">
					{criterion.criterionName}
					{criterion.criterionDescription && (
						<span
							className="text-gray-400 hover:text-gray-600 transition-colors"
							data-testid={`criterion-description-${criterion.rfxCriteriaId}`}
						>
							<InfoTooltip
								tooltipText={criterion.criterionDescription}
								size={14}
							/>
						</span>
					)}
				</span>
				<span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
					Weight: {criterion.criterionWeight}%
				</span>
				<div className="flex items-center gap-2">
					<label
						htmlFor={`score-${criterion.rfxCriteriaId}`}
						className="text-xs font-medium text-gray-600"
					>
						Score (0–{maxScore})
					</label>
					<div className="relative">
						<input
							id={`score-${criterion.rfxCriteriaId}`}
							type="number"
							min={0}
							max={maxScore}
							step="1"
							value={criterion.score ?? ""}
							onChange={handleScoreChange}
							disabled={readOnly}
							className="w-20 rounded-md border border-gray-300 px-2 py-1 pr-7 text-sm text-right focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
							data-testid={`score-input-${criterion.rfxCriteriaId}`}
							placeholder="—"
						/>
						{criterion.score !== null && !readOnly && (
							<button
								type="button"
								onClick={() => onScoreChange(criterion.rfxCriteriaId, null)}
								className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
								aria-label="Clear score"
								data-testid={`score-clear-${criterion.rfxCriteriaId}`}
							>
								<FiX size={12} />
							</button>
						)}
					</div>
					{showGuideHint && onShowGuide && (
						<button
							type="button"
							onClick={onShowGuide}
							className="text-blue-400 hover:text-blue-600 transition-colors"
							aria-label="Show scoring guide"
							data-testid={`guide-hint-${criterion.rfxCriteriaId}`}
						>
							<FiInfo size={14} />
						</button>
					)}
				</div>
			</div>

			<div data-testid={`comment-editor-${criterion.rfxCriteriaId}`}>
				<RichTextEditor
					value={criterion.comment ?? ""}
					onChange={handleCommentChange}
					disabled={readOnly}
					placeholder="Add comments..."
					minHeight="4rem"
					testId={`comment-rte-${criterion.rfxCriteriaId}`}
				/>
			</div>
		</div>
	);
};
